import { useContext, useCallback } from 'react';
import { ReceptionContext } from '../reception/ReceptionProvider';

// Hooks
import useNotify from "../notify";

// Service
import ProductsService from '../../services/productsService';
import MovementService from '../../services/movementService';
import ReferralService from '../../services/referralService';
import { utils } from '../../utils/utils';
import { stockControllUtils } from '../../utils/stockControll-utils';
import useUser from '../user/useUser';
const useReception = () => {
    const [{ movement, loading }, reducer] = useContext(ReceptionContext);
    const { Notify, variants } = useNotify();
    const { business } = useUser();
    const setMovement = useCallback((payload) =>
        reducer({ type: 'EDIT', payload })
    , [reducer]);

    const setLoading = ( payload ) =>
        reducer({ type: 'SET_LOADING', payload })

    // Search Products
    const searchProduct = async (refId) => {
        setLoading(true);
        const product = await ProductsService
            .GetByRefId(refId)
            .catch(() => console.error(`Product ${refId} not found.`))
            .finally(() => setLoading(false));

        if(!product)
            return;

        return {
            productId: product.productId,
            refId: product.refId,
            colorCode: product.colorCode,
            sizeCode: product.sizeCode || 'U',
            name: product.name
        }
    }
    
    const searchProducts = async (refIds) => {
        setLoading(true);
        const products = await ProductsService
            .GetAllByRefIds(refIds)
            .catch(() => console.error(`Product information could not be accessed`))
            .finally(() => setLoading(false));

        if(!products)
            return;

        return products.map(product => ({
            productId: product.productId,
            refId: product.refId,
            colorCode: product.colorCode,
            sizeCode: product.sizeCode || 'U',
            name: product.name
        }));
    }

    // Products
    const CreateProduct = (products, product, refId, quantity) => {
        const referral = utils
            .selectMany(products, 'referrals')
            .sort((a, b) => a.referralId - b.referralId)[0]

        const detail = {
            referrals: [{
                referralId: referral.referralId,
                plannedQuantity: 0,
                warehouseId: referral.warehouseId
            }],
            productId: product?.productId,
            name: product?.name || 'Producto No Encontrado',
            refId: product ? product.refId : refId,
            colorCode: product?.colorCode,
            sizeCode: product?.sizeCode,
            quantity,
            plannedQuantity: 0,
            difference: quantity,
            differenceToShow: `+${quantity}`,
            error: product === undefined
        };

        products.push(detail)
    }

    const addProduct = async ( refId ) => {
        const products = movement.products.map(d => d);
        const parsedRefId = stockControllUtils.parseRefId(refId);

        if (parsedRefId.error) 
            return Notify(parsedRefId.error, variants.ERROR);

        const product = products
            .find(detail => detail.refId === parsedRefId.sku);

        if(!product) {
            const newProduct = await searchProduct(parsedRefId.sku);
            if (!newProduct) 
                return Notify(`No se encontró el producto ${parsedRefId.sku}`, variants.ERROR);
            CreateProduct(products, newProduct, parsedRefId.sku, parsedRefId.quantity);
        } else update(product, parsedRefId.quantity);
        
        setMovement({ products })
        return true;
    }
    
    const addProductMassive = async (refIds) => {
        const products = movement.products.map(d => d);
        const parseRefIds = refIds.map(refId => stockControllUtils.parseRefId(refId));

        const newRefIds = parseRefIds
            .filter(item => !item.error && !products.some(detail => detail.refId === item.sku))
            .reduce((acc, item) => {
                const existingItem = acc.find(i => i.sku === item.sku);

                if (existingItem) {
                    existingItem.quantity += item.quantity;
                } else {
                    acc.push({ ...item });
                }

                return acc;
            }, []);

        const existingRefIds = parseRefIds
            .filter(item => !item.error)
            .filter(item => products
                .some(detail => detail.refId === item.sku))

        const notFoundRefIds = [];

        if( newRefIds.length > 0 ) {
            const newProducts = await searchProducts(newRefIds.map(r => r.sku));
            
            newRefIds.forEach(parsedRefId => {
                const product = newProducts.find(p => p.refId === parsedRefId.sku);
                if (product)
                    CreateProduct(products, product, parsedRefId.sku, parsedRefId.quantity);
                else notFoundRefIds.push(parsedRefId.sku);
            });
        }

        existingRefIds
            .forEach(parsedRefId => {
                const product = products
                    .find(detail => detail.refId === parsedRefId.sku);

                update(product, parsedRefId.quantity);
            });

        const refIdsWhitError = parseRefIds.filter(item => !!item.error);

        if( refIdsWhitError.length > 0 )
            utils.downloadOnTxt( refIdsWhitError, "skus-con-errores.txt" )

        setMovement({ products })

        if (notFoundRefIds.length > 0) {
            const link = document.createElement('a');
            link.download = 'NotFound-skus.txt';
            const blob = new Blob([notFoundRefIds.join('\n')], { type: 'text/plain' });
            link.href = window.URL.createObjectURL(blob);
            link.click();
        }
        
        return true;
    }

    const removeProductMassive = (refIds) => {
        const quantityBySku = refIds
            .map(refId => stockControllUtils.parseRefId(refId))
            .filter(item => !item.error)
            .reduce((acc, item) => {
                acc[item.sku] = (acc[item.sku] || 0) + item.quantity;
                return acc;
            }, {});

        const products = movement.products
            .map(product => {
                const quantityToRemove = quantityBySku[product.refId];
                if (!quantityToRemove)
                    return product;

                const quantity = product.quantity - quantityToRemove;
                const difference = stockControllUtils.calculateDiffernce(quantity, product.plannedQuantity);

                return {
                    ...product,
                    quantity,
                    difference,
                    differenceToShow: stockControllUtils.formatDiferrence(difference)
                };
            })
            .filter(product => product.quantity > 0 || product.plannedQuantity > 0 || product.referrals?.length > 0);

        setMovement({ products });
    }

    const editProduct = (refId, payload) => {
        const products = movement.products.map(product => {
            if(product.refId !== refId)
                return product

            return { ...product, ...payload };
        });

        setMovement({ products });
    }

    const removeProduct = ( refId ) => {
        const exist = movement.products
            .some(d => d.refId === refId)

        if(exist) 
            setMovement({ 
                products: movement.products
                    .filter(d => d.refId !== refId) 
            })
        
        return exist;
    }

    const SaveReception = async () => {
        const details = utils.selectMany([...movement.products]
            .map(product => {
                let accumulatedQuantity = 0;
                return product.referrals.map((referral, index) => {
                    let quantity = 0;
                    if(product.referrals.length > index + 1) {
                        accumulatedQuantity += referral.plannedQuantity
                        quantity = referral.plannedQuantity;
                    } else quantity = product.quantity - accumulatedQuantity;
                    
                    if(business.isFranchise) {
                        quantity = referral.plannedQuantity
                    }
                    
                    return {
                        ...referral,
                        colorCode: product.colorCode,
                        name: product.name,
                        productId: product.productId,
                        refId: product.refId,
                        sizeCode: product.sizeCode,
                        quantity
                    }
                });
            }))

        const req = { 
            ...movement,
            details
        };

        setLoading(true);
        if( movement.id )
            return MovementService
                .Update(req)
                .then(movement => reducer({ type: 'REPLACE', movement }))
                .finally(() => setLoading(false));

        return MovementService
            .CreateReceptions(req)
            .finally(() => setLoading(false));
    }

    const update = (product, quantity) => {
        product.quantity += quantity;
        product.difference = stockControllUtils.calculateDiffernce(product.quantity, product.plannedQuantity);
        product.differenceToShow = stockControllUtils.formatDiferrence(product.difference);
    }

    // Referral Methods
    const addReferral = async ( referralIds ) => {
        setLoading(true);

        let newDetails = await Promise.all(
            referralIds
                .filter(referralId => !movement.referrals.some(detail => detail.referralId === referralId))
                .map( async referralId => await ReferralService.GetReferralDetails(referralId))
        )

        newDetails = utils.selectMany(newDetails);

        const detailsGrouped = utils.groupBy(newDetails, 'productId');
        const newProducts = Object
            .keys(detailsGrouped)
            .map(key => {
                const products = detailsGrouped[key];
                const quantity = utils.sum(products, 'quantity');
                const plannedQuantity = utils.sum(products, 'plannedQuantity');
                const difference = stockControllUtils.calculateDiffernce(quantity, plannedQuantity)

                return {
                    referrals: products.map(product => ({
                        referralId: product.referralId,
                        plannedQuantity: product.plannedQuantity,
                        warehouseId: product.warehouseId
                    })),
                    productId: products[0].productId,
                    refId: products[0].refId,
                    colorCode: products[0].colorCode,
                    sizeCode: products[0].sizeCode,
                    name: products[0].name,
                    plannedQuantity,
                    quantity,
                    difference,
                    differenceToShow: stockControllUtils.formatDiferrence(difference)
                }
            });

        let products = [...movement.products];

        newProducts.forEach(p => {
            const product = products.find(product => product.productId === p.productId)

            if(product) {
                product.referrals = [...product.referrals, ...p.referrals]
                    .sort((a, b) => a.referralId - b.referralId);
                product.plannedQuantity += p.plannedQuantity;
                product.difference = stockControllUtils.calculateDiffernce(product.quantity, product.plannedQuantity);
                product.differenceToShow = stockControllUtils.formatDiferrence(product.difference);
                return;
            }

            products = [...products, p]
        });

        const newReferrals = [...referralIds, ...movement.referrals
            .filter(referral => !referralIds.some(d => d.referralId === referral.referralId))]
            .sort((a, b) => a.referralId - b.referralId)

        setLoading(false)
        setMovement({ 
            products,
            referrals: newReferrals,
        })
    }

    const removeReferral = ( referralId ) => {
        setLoading(true)
        const details = movement.details
            .filter(detail => detail.referralId !== referralId)

        const referrals = movement.referrals
            .filter(referral => referral !== referralId)

        const products = movement.products
            .map(product => {
                const referrals = product.referrals
                    .filter(referral => referral.referralId !== referralId);

                const plannedQuantity = utils.sum(referrals, 'plannedQuantity');
                const difference = stockControllUtils.calculateDiffernce(product.quantity, plannedQuantity)

                return {
                    ...product,
                    referrals,
                    plannedQuantity,
                    difference,
                    differenceToShow: stockControllUtils.formatDiferrence(difference)
                }
            })
            .filter(product => product.referrals.length > 0)

        setMovement({ details, referrals, products })
        setLoading(false)
    }

    const exportDetails = async (details) => {
        setLoading(true);
        const result = await MovementService.ExportReception(details);
        setLoading(false);

        return result;
    }

    return {
        movement,
        loading,
        edit: setMovement,
        save: SaveReception,

        addProduct,
        addProductMassive,
        editProduct,
        removeProduct,
        removeProductMassive,

        addReferral,
        removeReferral,
        exportDetails
    };
}

export default useReception;